import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import useMatchMedia from 'hooks/useMatchMedia';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  DropdownProps,
  DropdownRef,
} from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { Dropdown, Modal } from 'ncoded-component-library';

import './ResponsiveDropdown.styles.scss';

export type ResponsiveDropdownRef = {
  close: () => void;
};

type ResponsiveDropdownProps = {
  children?: ReactNode;
  className?: string;
  showModal?: boolean;
  trigger: JSX.Element;
  modalProps?: Partial<ModalProps>;
  dropdownProps?: Partial<Omit<DropdownProps, 'ref'>>;
};

const ResponsiveDropdown: React.ForwardRefRenderFunction<
  ResponsiveDropdownRef,
  ResponsiveDropdownProps
> = (props, ref) => {
  const isPhablet = useMatchMedia('isPhablet');

  const {
    children,
    className,
    trigger,
    modalProps,
    dropdownProps,
    showModal = isPhablet,
  } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  const classes = classNames('responsive-dropdown', className);

  const open = useCallback(() => {
    if (showModal) modal?.open();
    else dropdown?.setIsOpen(true);
  }, [dropdown, modal, showModal]);

  const close = useCallback(() => {
    if (showModal) modal?.close();
    else dropdown?.setIsOpen(false);
  }, [dropdown, modal, showModal]);

  useImperativeHandle(ref, () => ({ close }), [close]);

  const Trigger = useMemo(
    () => (
      <div
        className={classes}
        onClick={(e) => {
          e.stopPropagation();
          open();
        }}
      >
        {trigger}
      </div>
    ),
    [classes, open, trigger],
  );

  if (showModal) {
    return (
      <>
        {Trigger}
        <Modal ref={modalRef} {...modalProps}>
          {children}
        </Modal>
      </>
    );
  }

  return (
    <Dropdown
      ref={dropdownRef}
      trigger={Trigger}
      renderAsPortal
      {...dropdownProps}
      portalClassName={classNames(
        'responsive-dropdown__portal',
        dropdownProps?.portalClassName,
      )}
    >
      {children}
    </Dropdown>
  );
};

export default forwardRef(ResponsiveDropdown);
